
import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, PlusCircle, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export default function PaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const { customerId } = useSubscription();

  // Fetch payment methods when component mounts
  useState(() => {
    if (customerId) {
      fetchPaymentMethods();
    }
  });

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await supabase.functions.invoke('stripe-functions', {
        body: { 
          action: 'getPaymentMethods', 
          customerId 
        },
      });
      
      if (response.error) throw new Error(response.error.message);
      
      setPaymentMethods(response.data.data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    if (!customerId) return;
    
    setIsAdding(true);
    
    try {
      const response = await supabase.functions.invoke('stripe-functions', {
        body: { 
          action: 'createSetupSession', 
          customerId 
        },
      });
      
      if (response.error) throw new Error(response.error.message);
      
      // Redirect to Stripe setup
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error setting up payment method:', error);
      toast.error('Failed to set up payment method');
      setIsAdding(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    setIsDeleting(paymentMethodId);
    
    try {
      const response = await supabase.functions.invoke('stripe-functions', {
        body: { 
          action: 'deletePaymentMethod', 
          paymentMethodId 
        },
      });
      
      if (response.error) throw new Error(response.error.message);
      
      toast.success('Payment method removed successfully');
      // Update the payment methods list
      setPaymentMethods(methods => methods.filter(method => method.id !== paymentMethodId));
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error('Failed to remove payment method');
    } finally {
      setIsDeleting(null);
    }
  };

  const getCardIcon = (brand: string) => {
    return <CreditCard className="h-4 w-4 mr-2" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Methods</CardTitle>
        <CardDescription>Manage your payment methods for billing</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : paymentMethods.length > 0 ? (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center justify-between p-3 border rounded-md">
                <div className="flex items-center">
                  {getCardIcon(method.brand)}
                  <span className="font-medium">
                    {method.brand.charAt(0).toUpperCase() + method.brand.slice(1)} ending in {method.last4}
                  </span>
                  <span className="text-sm text-muted-foreground ml-2">
                    {method.expMonth}/{method.expYear}
                  </span>
                  {method.isDefault && (
                    <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      Default
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeletePaymentMethod(method.id)}
                  disabled={isDeleting === method.id || paymentMethods.length === 1}
                >
                  {isDeleting === method.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground">No payment methods found</p>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button 
          className="w-full" 
          onClick={handleAddPaymentMethod}
          disabled={isAdding}
        >
          {isAdding ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <PlusCircle className="mr-2 h-4 w-4" />
          )}
          Add Payment Method
        </Button>
      </CardFooter>
    </Card>
  );
}
